import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { APP_WIDTH, APP_HEIGHT, CARD_WIDTH, CARD_HEIGHT, getProxiedCardImageUrl } from '../../utils';

import Tappable from '../../common/containers/Tappable';
import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';
import TappableTextureButton from '../../common/containers/TappableTextureButton';
import ThreeDimensionalCardContainer from '../../gameplay/containers/threeDimensionalCardContainer';
import { playPrefabClip, preloadPrefabClips } from '../../animations/prefab';
import { packOpenPrefab } from '../../animations/prefabs/packOpen';

const PACK_SCALE = 1;
const STAGE_Y = 1150;
const HERO_SCALE = 4.3;
const TITLE_Y = 330;
const COUNTER_Y = TITLE_Y + 80;
const HINT_Y = 1850;
const GRID_CENTER_Y = 1100;
const GRID_MAX_WIDTH = 920;
const GRID_MAX_HEIGHT = 1250;
const GRID_GAP = 26;
const FOOTER_Y = 2060;
const BUTTON_WIDTH = 440;
const BUTTON_HEIGHT = 130;
const SKIP_SIZE = 176;
const SKIP_SPEED = 6;
const HERO_BADGE_WIDTH = 188;
const HERO_BADGE_HEIGHT = 70;
const GRID_BADGE_WIDTH = 118;
const GRID_BADGE_HEIGHT = 46;

// Card origin is its center. Positive insets pull the badge onto the card; negative insets hang it past the edge.
const HERO_BADGE_ANCHOR = 'top-left';
const HERO_BADGE_INSET_X = 0;
const HERO_BADGE_INSET_Y = -HERO_BADGE_HEIGHT - 20;
const GRID_BADGE_ANCHOR = 'top-left';
const GRID_BADGE_INSET_X = 8;
const GRID_BADGE_INSET_Y = 8;

const badgeTopLeft = (cardWidth, cardHeight, badgeWidth, badgeHeight, anchor, insetX, insetY) => {
    const [vertical, horizontal] = anchor.split('-');
    const x = horizontal === 'left'
        ? -cardWidth / 2 + insetX
        : horizontal === 'right'
            ? cardWidth / 2 - badgeWidth - insetX
            : -badgeWidth / 2 + insetX;
    const y = vertical === 'top'
        ? -cardHeight / 2 + insetY
        : vertical === 'bottom'
            ? cardHeight / 2 - badgeHeight - insetY
            : -badgeHeight / 2 + insetY;
    return { x, y };
};

// rarities listed here spin and pop when they land on top of the stack
const FLOURISH_RARITIES = ['L', 'SR', 'SEC'];

const shouldFlourish = (card) => {
    if (!card) return false;
    if (card.isAltArt || /-Alt$/i.test(card.rarity || '')) return true;
    return FLOURISH_RARITIES.includes(card.rarity);
};

// server pack results carry the full catalog card, but tolerate a bare { id } pull
const withImgUrl = (card) => (card?.imgUrl ? card : { ...card, imgUrl: `${card?.id}.png` });

/** The pack art comes as two pieces so the tear strip can come off on its own. */
class BoosterPack extends PIXI.Container {
    constructor() {
        super();
        this.label = 'boosterPack';

        this.packWhole = new PIXI.Sprite(PIXI.Assets.get('op01PackWhole'));
        this.packWhole.anchor.set(0.5, 0);
        this.packWhole.scale.set(1.283);
        this.packWhole.y = -73;
        this.addChild(this.packWhole);

        this.tearBottom = new PIXI.Sprite(PIXI.Assets.get('op01TearBottom'));
        this.tearBottom.anchor.set(0.5, 0);
        this.tearBottom.y = 100;
        this.addChild(this.tearBottom);

        this.tearTop = new PIXI.Sprite(PIXI.Assets.get('op01TearTop'));
        this.tearTop.anchor.set(0.5, 0);
        this.addChild(this.tearTop);
    }
}

export default class PackOpenAnimatorContainer extends PIXI.Container {
    constructor(packOpenResult, opts = {}) {
        super();
        this.label = 'packOpenAnimatorContainer';

        const openedCards = Array.isArray(packOpenResult) ? packOpenResult : packOpenResult?.openedCards;
        this.cards = (openedCards || []).map(withImgUrl);
        this.packName = (Array.isArray(packOpenResult) ? null : packOpenResult?.name) || 'BOOSTER PACK';
        this.screenWidth = opts.width || APP_WIDTH;
        this.screenHeight = opts.height || APP_HEIGHT;

        this.state = 'idle';
        this.hero = null;
        this.nextHero = null;
        this.newBadge = null;
        this.badgePop = null;
        this.cardIndex = 0;
        this.speed = 1;
        this.isAnimating = false;
        this.timelines = new Set();
    }

    static async loadStaticAssets() {
        await Promise.all([
            PIXI.Assets.load([
                { src: '/assets/shop/op01_pack_whole.png', alias: 'op01PackWhole' },
                { src: '/assets/shop/op01_tear_top.png', alias: 'op01TearTop' },
                { src: '/assets/shop/op01_tear_bottom.png', alias: 'op01TearBottom' },
                { src: '/assets/shop/skip_button.png', alias: 'skipButton' },
            ]),
            ThreeDimensionalCardContainer.loadAssets(),
        ]);
    }

    static createPreview(cards) {
        const preview = new PackOpenAnimatorContainer(cards, { width: APP_WIDTH, height: APP_HEIGHT });
        preview.buildMainLayout();
        preview.hero = preview.buildHeroCard(preview.cards[0]);
        preview.stackNextCard();
        if (preview.nextHero) preview.nextHero.visible = true;
        preview.skipButton.visible = true;
        preview.collectButton.visible = true;
        preview.hintText.visible = true;
        preview.hintText.text = 'TAP TO OPEN';
        preview.updateCounter();
        if (preview.cards[0]) preview.cards[0].isNew = true;
        preview.showNewBadge();
        preview.settleNewBadge();
        return preview;
    }

    static async loadCardAssets(cards) {
        const assets = (cards || [])
            .filter((card) => card?.imgUrl || card?.id)
            .map((card) => ({ src: getProxiedCardImageUrl(withImgUrl(card).imgUrl) }));
        if (assets.length > 0) {
            await PIXI.Assets.load(assets);
        }
    }

    async loadAssets() {
        await Promise.all([
            PackOpenAnimatorContainer.loadStaticAssets(),
            PackOpenAnimatorContainer.loadCardAssets(this.cards),
            preloadPrefabClips(packOpenPrefab),
        ]);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this);
    }

    async playClip(name, { wait = true } = {}) {
        return playPrefabClip(packOpenPrefab, name, this, {
            wait,
            createTimeline: () => this.timeline(),
        });
    }

    /** Every animation runs through here so `skipRemaining` and `destroy` can steer them all. */
    timeline(vars) {
        const timeline = gsap.timeline(vars).timeScale(this.speed);
        this.timelines.add(timeline);
        timeline.eventCallback('onComplete', () => this.timelines.delete(timeline));
        return timeline;
    }

    cardTexture(card) {
        return PIXI.Assets.get(getProxiedCardImageUrl(card.imgUrl));
    }

    gridMetrics() {
        const count = Math.max(this.cards.length, 1);
        const columns = Math.min(count, count > 6 ? 4 : 3);
        const rows = Math.ceil(count / columns);
        const scale = Math.min(
            (GRID_MAX_WIDTH - GRID_GAP * (columns - 1)) / columns / CARD_WIDTH,
            (GRID_MAX_HEIGHT - GRID_GAP * (rows - 1)) / rows / CARD_HEIGHT,
            1.5,
        );
        const height = rows * (CARD_HEIGHT * scale + GRID_GAP) - GRID_GAP;

        return { columns, rows, scale, top: GRID_CENTER_Y - height / 2 };
    }

    gridSlotPosition(index) {
        const { columns, scale, top } = this.grid;
        const cellWidth = CARD_WIDTH * scale + GRID_GAP;
        const cellHeight = CARD_HEIGHT * scale + GRID_GAP;
        const row = Math.floor(index / columns);
        const column = index % columns;
        const columnsInRow = Math.min(columns, this.cards.length - row * columns);

        return {
            x: (column - (columnsInRow - 1) / 2) * cellWidth,
            y: top + row * cellHeight + (CARD_HEIGHT * scale) / 2,
        };
    }

    buildText(text, y, style) {
        const pixiText = new PIXI.Text({
            text,
            style: {
                fontFamily: 'Open Sans',
                fontWeight: '700',
                fill: '#ffffff',
                align: 'center',
                ...style,
            },
        });
        pixiText.anchor.set(0.5);
        pixiText.y = y;
        this.content.addChild(pixiText);

        return pixiText;
    }

    buildCollectButton() {
        const fill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#5A3607' },
                { offset: 1, color: '#92620F' },
            ],
        });

        const borderFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#AA6512' },
                { offset: 1, color: '#DA9C1B' },
            ],
        });

        const fontFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        const button = new TappableGraphicsButton({
            text: 'COLLECT',
            width: BUTTON_WIDTH,
            height: BUTTON_HEIGHT,
            fill,
            borderFill,
            fontFill,
            fontStrokeColor: '#422400',
            fontStrokeWidth: 5,
            fontSize: 50,
            fontFamily: 'Caladea',
            cornerRadius: 27,
            borderWidth: 4,
        });
        button.x = -BUTTON_WIDTH / 2;
        button.y = FOOTER_Y - BUTTON_HEIGHT / 2;
        button.visible = false;
        this.content.addChild(button);

        return button;
    }

    buildMainLayout() {
        this.badgePop?.kill();
        this.badgePop = null;
        this.newBadge = null;
        for (const child of this.removeChildren()) {
            child.destroy({ children: true });
        }
        this.grid = this.gridMetrics();

        this.backdrop = new Tappable();
        this.backdrop.label = 'packOpenBackdrop';
        this.backdrop.onTap = () => this.onBackdropTap();
        this.backdrop.onSwipeLeft = () => this.swipeCard('swipe-card-left');
        this.backdrop.onSwipeRight = () => this.swipeCard('swipe-card-right');
        this.addChild(this.backdrop);

        this.backdropGraphics = new PIXI.Graphics();
        this.backdrop.addChild(this.backdropGraphics);

        this.content = new PIXI.Container();
        this.content.label = 'packOpenContent';
        this.addChild(this.content);

        this.heroLayer = new PIXI.Container();
        this.heroLayer.label = 'packOpenHeroLayer';
        this.content.addChild(this.heroLayer);

        this.gridLayer = new PIXI.Container();
        this.gridLayer.label = 'packOpenGridLayer';
        this.content.addChild(this.gridLayer);

        this.pack = new BoosterPack();
        this.heroLayer.addChild(this.pack);

        this.titleText = this.buildText(this.packName.toUpperCase(), TITLE_Y, { fontSize: 50, letterSpacing: 2 });
        this.counterText = this.buildText('', COUNTER_Y, { fontSize: 34, fontWeight: '600', fill: '#a8bcda', letterSpacing: 4 });
        this.hintText = this.buildText('', HINT_Y, { fontSize: 36, fontWeight: '600', fill: '#dce6f5', letterSpacing: 4 });
        this.hintText.visible = false;

        this.skipButton = new TappableTextureButton({
            texture: PIXI.Assets.get('skipButton'),
            width: SKIP_SIZE,
            height: SKIP_SIZE,
            text: 'SKIP',
            fontSize: 34,
            fontWeight: '700',
            textOffsetY: 120,
        });
        this.skipButton.visible = false;
        this.skipButton.onButtonTap = () => this.skipRemaining();
        this.content.addChild(this.skipButton);

        this.collectButton = this.buildCollectButton();
        this.collectButton.onButtonTap = () => this.onCollect?.();

        this.resize(this.screenWidth, this.screenHeight);
    }

    resize(width, height) {
        this.screenWidth = width;
        this.screenHeight = height;
        if (!this.backdropGraphics) {
            return;
        }

        this.backdropGraphics.clear();
        this.backdropGraphics.rect(0, 0, width, height);
        this.backdropGraphics.fill({ color: '#05070d', alpha: 0.93 });

        this.content.x = width / 2;
        if (this.skipButton) {
            this.skipButton.x = width / 2 - SKIP_SIZE - 50;
            this.skipButton.y = height - SKIP_SIZE - 120;
        }
    }

    async start() {
        this.buildMainLayout();
        this.state = 'start';
        await this.playClip('start');
        const idle = await this.playClip('pack-idle', { wait: false });
        this.packIdleTimeline = idle.timeline;
        this.blinkHint('TAP TO OPEN');
        this.state = 'pack';
    }

    onBackdropTap() {
        if (this.state === 'pack') {
            this.openPack();
        } else if (this.state === 'cards') {
            this.nextCard();
        }
    }

    blinkHint(text) {
        this.hintTween?.kill();
        this.hintText.text = text;
        this.hintText.alpha = 1;
        this.hintText.visible = true;
        this.hintTween = gsap.to(this.hintText, {
            duration: 0.9,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            pixi: { alpha: 0.25 },
        });
    }

    hideHint() {
        this.hintTween?.kill();
        this.hintTween = null;
        this.hintText.visible = false;
    }

    updateCounter() {
        this.counterText.text = `CARD ${Math.min(this.cardIndex + 1, this.cards.length)} OF ${this.cards.length}`;
    }

    async openPack() {
        if (this.state !== 'pack' || this.cards.length === 0) {
            return;
        }

        this.state = 'cards';
        this.isAnimating = true;
        this.packIdleTimeline?.kill();
        this.packIdleTimeline = null;
        this.hideHint();
        this.updateCounter();
        this.skipButton.visible = this.cards.length > 1;

        this.hero = this.buildHeroCard(this.cards[this.cardIndex]);
        this.stackNextCard();
        // this.pack.packWhole.visible = false;
        // this.pack.tearTop.visible = true;
        // this.pack.tearBottom.visible = true;

        await this.playClip('open-pack');
        this.pack.visible = false;
        await this.playRarityFlourish();
        this.showNewBadge();
        this.isAnimating = false;

        await this.advanceIfSkipping();
    }

    /** Cards share one spot on stage; new ones go underneath so only the top card is visible. */
    buildHeroCard(card) {
        const hero = new ThreeDimensionalCardContainer(card);
        hero.label = `packOpenHeroCard-${card.id}`;
        hero.y = STAGE_Y;
        hero.scale.set(HERO_SCALE);
        this.heroLayer.addChildAt(hero, 0);

        return hero;
    }

    /** Keeps the card after the current one hidden behind it, ready to be uncovered. */
    stackNextCard() {
        const card = this.cards[this.cardIndex + 1];
        this.nextHero = card ? this.buildHeroCard(card) : null;
    }

    /** Spins and pops the top card so the player feels a rare pull is special. */
    async playRarityFlourish() {
        const hero = this.hero;
        if (!hero || !shouldFlourish(this.cards[this.cardIndex])) {
            return;
        }

        // the stack behind would peek out while the card turns edge-on
        const stacked = this.nextHero;
        if (stacked) {
            stacked.visible = false;
        }

        await this.playClip('flourish');

        if (stacked) {
            stacked.visible = true;
        }
    }

    /** Skipping keeps flicking the stack on its own, otherwise we wait for the player's tap. */
    async advanceIfSkipping() {
        if (this.speed > 1) {
            await this.nextCard();
        }
    }

    swipeCard(clipName) {
        if (this.state !== 'cards') return;
        this.advanceCard(clipName);
    }

    /** Flicks the top card off stage, uncovering the one stacked behind it. */
    nextCard() {
        return this.advanceCard('next-card');
    }

    async advanceCard(clipName) {
        if (this.isAnimating || !this.hero) {
            return;
        }

        this.isAnimating = true;
        this.settleNewBadge();

        const outgoing = this.hero;
        await this.playClip(clipName);
        this.clearNewBadge();

        this.hero = this.nextHero;
        this.nextHero = null;
        this.cardIndex += 1;
        this.updateCounter();
        if (this.hero) this.stackNextCard();

        outgoing.destroy({ children: true });
        await this.playRarityFlourish();
        this.showNewBadge();
        this.isAnimating = false;

        if (this.hero) {
            await this.advanceIfSkipping();
        } else {
            this.showSummary();
        }
    }

    skipRemaining() {
        if (this.state !== 'cards') {
            return;
        }

        this.speed = SKIP_SPEED;
        this.skipButton.visible = false;
        this.hideHint();
        for (const timeline of this.timelines) {
            timeline.timeScale(this.speed);
        }
        this.nextCard();
    }

    buildGridCard(card, slot) {
        const gridCard = new PIXI.Container();
        gridCard.label = `packOpenGridCard-${card.id}`;
        gridCard.x = slot.x;
        gridCard.y = slot.y;
        this.gridLayer.addChild(gridCard);

        const sprite = new PIXI.Sprite(this.cardTexture(card));
        sprite.anchor.set(0.5);
        sprite.width = CARD_WIDTH * this.grid.scale;
        sprite.height = CARD_HEIGHT * this.grid.scale;
        gridCard.addChild(sprite);

        if (card.isNew) {
            const badge = this.buildNewBadge(GRID_BADGE_WIDTH, GRID_BADGE_HEIGHT);
            const spot = badgeTopLeft(
                sprite.width,
                sprite.height,
                GRID_BADGE_WIDTH,
                GRID_BADGE_HEIGHT,
                GRID_BADGE_ANCHOR,
                GRID_BADGE_INSET_X,
                GRID_BADGE_INSET_Y,
            );
            badge.position.set(spot.x, spot.y);
            gridCard.addChild(badge);
        }

        return gridCard;
    }

    buildNewBadge(width, height) {
        const badge = new PIXI.Container();
        badge.label = 'newCardBadge';
        badge.eventMode = 'none';
        badge.interactiveChildren = false;

        const background = new PIXI.Graphics();
        background.roundRect(0, 0, width, height, height / 2);
        background.fill({ color: '#B0001D', alpha: 1 });
        background.stroke({ color: '#FF0000', width: Math.max(3, Math.round(height * 0.07)) });
        badge.addChild(background);

        const label = new PIXI.Text({
            text: 'NEW',
            style: {
                fontFamily: 'Open Sans',
                fontSize: Math.round(height * 0.48),
                fontWeight: 'bold',
                fill: '#fffaf0',
                letterSpacing: 1.5,
            },
        });
        label.anchor.set(0.5);
        label.x = width / 2;
        label.y = height / 2;
        badge.addChild(label);

        return badge;
    }

    /** Pops a NEW sticker above the revealed card when this pull was not already owned. */
    showNewBadge() {
        const card = this.cards[this.cardIndex];
        const hero = this.hero;
        this.clearNewBadge();
        if (!hero || !card?.isNew) {
            return;
        }

        const cardScale = hero.scale.x || HERO_SCALE;
        const badge = this.buildNewBadge(HERO_BADGE_WIDTH, HERO_BADGE_HEIGHT);
        badge.pivot.set(HERO_BADGE_WIDTH / 2, HERO_BADGE_HEIGHT / 2);
        const spot = badgeTopLeft(
            CARD_WIDTH * cardScale,
            CARD_HEIGHT * cardScale,
            HERO_BADGE_WIDTH,
            HERO_BADGE_HEIGHT,
            HERO_BADGE_ANCHOR,
            HERO_BADGE_INSET_X,
            HERO_BADGE_INSET_Y,
        );
        badge.x = hero.x + spot.x + HERO_BADGE_WIDTH / 2;
        badge.y = hero.y + spot.y + HERO_BADGE_HEIGHT / 2;
        this.heroLayer.addChild(badge);
        this.newBadge = badge;

        badge.alpha = 0;
        badge.scale.set(0.35);
        this.badgePop = this.timeline().to(badge, {
            duration: 0.34,
            ease: 'back.out(2)',
            pixi: { alpha: 1, scale: 1 },
        });
    }

    /** Lets the next-card clip start from the settled sticker instead of mid-pop. */
    settleNewBadge() {
        if (!this.badgePop) return;
        this.badgePop.progress(1);
        this.badgePop = null;
    }

    clearNewBadge() {
        this.badgePop?.kill();
        this.badgePop = null;
        this.newBadge?.destroy({ children: true });
        this.newBadge = null;
    }

    showSummary() {
        this.state = 'done';
        this.speed = 1;
        this.hideHint();
        this.skipButton.visible = false;
        this.collectButton.visible = true;
        this.counterText.text = `Opening Results`;

        const gridCards = this.cards.map((card, index) => this.buildGridCard(card, this.gridSlotPosition(index)));
        this.timeline().from(gridCards, {
            duration: 0.4,
            ease: 'back.out(1.8)',
            stagger: 0.07,
            pixi: { alpha: 0, scale: 0.6 },
        });
        this.playClip('show-summary');
    }

    destroy(options) {
        for (const timeline of this.timelines) {
            timeline.kill();
        }
        this.timelines.clear();
        this.hintTween?.kill();
        super.destroy(options);
    }
}
